import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { axiosInstancev1 } from "../lib/axiosInstance";
import RealEstateNavbar from "../components/realestateagents/RealEstateNavbar";

import {
    MapPin, IndianRupee, Bed, Bath, Expand, Search, Filter, X,
    Home, Map, Building2, ChevronDown, Grid, List, Heart
} from "lucide-react";

/**
 * PropertySearch View
 * A dedicated search results page with a comprehensive left sidebar filter
 * and a right side property grid.
 */
const PropertySearch = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    // State for properties and loading
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(false);
    const [viewMode, setViewMode] = useState("grid"); // grid or list
    const [searchMode, setSearchMode] = useState('exact'); // exact, radius, or none
    const [searchLocation, setSearchLocation] = useState(null);
    const [radiusKm, setRadiusKm] = useState(null);
    const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

    // Sidebar Filter States (Initialized from query params if available)
    const [propertyType, setPropertyType] = useState(searchParams.get("type") || "house");
    const [lookingFor, setLookingFor] = useState(searchParams.get("filter") || "sale");
    const [city, setCity] = useState("");
    const [area, setArea] = useState("");
    const [budget, setBudget] = useState("");

    // Options from user images
    const houseTypes = ["Apartment", "Villa", "Flat", "Independent House", "Studio", "Row House", "Duplex", "Penthouse", "Farm House"];
    const landTypes = ["Residential Land", "Commercial Land", "Industrial Land", "Agricultural Land", "Farm Land", "DTCP Approved", "CMDA Approved"];

    const [selectedSubTypes, setSelectedSubTypes] = useState(() => {
        const pt = searchParams.get("propertyType");
        return pt ? [pt] : [];
    });
    const [bhk, setBhk] = useState(searchParams.get("bhk") || "");
    const [minPrice, setMinPrice] = useState(searchParams.get("minPrice") || "");
    const [maxPrice, setMaxPrice] = useState(searchParams.get("maxPrice") || "");
    const [minArea, setMinArea] = useState(searchParams.get("minArea") || "");
    const [maxArea, setMaxArea] = useState(searchParams.get("maxArea") || "");
    const [floor, setFloor] = useState(searchParams.get("floor_number") || "");
    const [facing, setFacing] = useState(searchParams.get("facing_direction") || "");

    useEffect(() => {
        window.scrollTo(0, 0);
        // Sync internal states with URL params
        const typeInUrl = searchParams.get("type") || "house";
        const filterInUrl = searchParams.get("filter") || "sale";
        const subTypeInUrl = searchParams.get("propertyType");

        setPropertyType(typeInUrl);
        setLookingFor(filterInUrl);
        if (subTypeInUrl) {
            setSelectedSubTypes([subTypeInUrl]);
        } else {
            setSelectedSubTypes([]);
        }

        fetchProperties();
    }, [searchParams]);

    const fetchProperties = async () => {
        setLoading(true);
        try {
            const type = searchParams.get("type") || "house";
            const filter = searchParams.get("filter") || "";
            const keyword = searchParams.get("keyword") || "";
            const location = searchParams.get("location") || "";
            const radius = searchParams.get("radius") || "";
            const bhkVal = searchParams.get("bhk") || "";
            const minP = searchParams.get("minPrice") || "";
            const maxP = searchParams.get("maxPrice") || "";
            const minA = searchParams.get("minArea") || "";
            const maxA = searchParams.get("maxArea") || "";
            const fl = searchParams.get("floor_number") || "";
            const fc = searchParams.get("facing_direction") || "";

            const dtcp = searchParams.get("dtcp") || "";
            const cmda = searchParams.get("cmda") || "";
            const rera = searchParams.get("rera") || "";
            const plotType = searchParams.get("plot_type") || "";

            const params = {
                property_category: (type === "plot" || type === "land") ? "plot" : "housing",
                status: "active",
                listing_type: filter,
                limit: 50,
            };

            // Add keyword-based search if provided
            if (keyword) {
                params.keyword = keyword;
            }

            // Add location-based search if provided (legacy or separate)
            if (location) {
                params.location = location;
            }

            // Add radius if provided
            if (radius) {
                params.radius = radius;
            }

            if (bhkVal) params.bhk = bhkVal;
            if (minP) params.minPrice = minP;
            if (maxP) params.maxPrice = maxP;
            if (minA) params.minArea = minA;
            if (maxA) params.maxArea = maxA;
            if (fl) params.floor_number = fl;
            if (fc) params.facing_direction = fc;
            if (dtcp) params.dtcp = dtcp;
            if (cmda) params.cmda = cmda;
            if (rera) params.rera = rera;
            if (plotType) params.plot_type = plotType;

            const response = await axiosInstancev1.get("/properties/search", { params });

            if (response.data.success) {
                // Update search mode info
                setSearchMode(response.data.searchMode || 'exact');
                setSearchLocation(response.data.searchLocation || null);
                setRadiusKm(response.data.radiusKm || null);

                const mapped = (response.data.properties || []).map((p) => {
                    let imageUrl = p.thumbnail || "/assets/best-deals/apt1.jpg";
                    if (imageUrl && !imageUrl.startsWith("http") && !imageUrl.startsWith("/")) {
                        imageUrl = `/${imageUrl}`;
                    }

                    const addr = [p.street_address, p.locality, p.city].filter(Boolean).join(", ") || "Location not specified";

                    // Determines title safely
                    const title = p.property_title || p.plot_title || p.property_type || "No Title";

                    // Determine price
                    const price = p.expected_price || p.plot_price || p.price || 0;

                    // Determine area
                    const area = p.built_up_area || p.plot_area || p.area || 0;

                    return {
                        id: p.id,
                        title: title,
                        address: addr,
                        price: price,
                        image: imageUrl,
                        category: p.property_category,
                        listingType: p.listing_type,
                        bedrooms: p.bedrooms || 0,
                        bathrooms: p.bathrooms || 0,
                        area: area,
                        areaUnit: p.area_unit || p.plot_area_unit || "sqft",
                        type: p.property_type || p.plot_type || "",
                    };
                });
                setProperties(mapped);
            } else {
                setProperties([]);
            }
        } catch (error) {
            console.error("Error fetching properties:", error);
            setProperties([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        const newParams = new URLSearchParams(searchParams);
        if (value) {
            newParams.set(key, value);
        } else {
            newParams.delete(key);
        }

        // If switching property type, clear sub-category selections
        if (key === "type") {
            setSelectedSubTypes([]);
        }

        setSearchParams(newParams);
    };

    const toggleSubType = (type) => {
        if (selectedSubTypes.includes(type)) {
            setSelectedSubTypes(selectedSubTypes.filter(t => t !== type));
        } else {
            setSelectedSubTypes([...selectedSubTypes, type]);
        }
    };

    const filteredProperties = useMemo(() => {
        return properties.filter(p => {
            // Check property type category match
            if (propertyType && propertyType !== "all") {
                const expectedCategory = propertyType === "house" ? "housing" : "plot";
                // If the selected main filter doesn't match the property category, skip it
                // Note: building type is treated as housing for now or needs separate logic
                if (propertyType === "building" && p.category !== "housing") return false;
                if (propertyType !== "building" && p.category !== expectedCategory) return false;
            }

            if (selectedSubTypes.length > 0 && !selectedSubTypes.includes(p.type)) {
                return false;
            }
            return true;
        });
    }, [properties, selectedSubTypes, propertyType]);

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <RealEstateNavbar forceWhite={true} />
            {/* Main Content Area */}
            <main className="flex-1 max-w-7xl mx-auto w-full px-4 py-8 pt-20 flex gap-8">

                {/* Mobile Filter Overlay Background */}
                {mobileFilterOpen && (
                    <div
                        className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                        onClick={() => setMobileFilterOpen(false)}
                    />
                )}

                {/* LEFT SIDEBAR - FILTERS (Desktop & Mobile Slide-out) */}
                <aside className={`
                    fixed lg:relative inset-y-0 left-0 z-50 lg:z-auto
                    w-[85%] max-w-xs lg:w-72 
                    bg-white rounded-none lg:rounded-2xl shadow-xl lg:shadow-sm border-r lg:border border-gray-100 
                    p-6 overflow-y-auto
                    transform transition-transform duration-300 ease-in-out
                    ${mobileFilterOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
                    lg:block lg:flex-shrink-0 lg:self-start lg:sticky lg:top-24
                `}>
                    {/* Mobile Close Button */}
                    <button
                        className="lg:hidden absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-700"
                        onClick={() => setMobileFilterOpen(false)}
                    >
                        <X size={24} />
                    </button>

                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-bold text-gray-900">Filters</h2>
                        <button
                            className="text-blue-600 text-sm font-medium hover:underline"
                            onClick={() => {
                                setSelectedSubTypes([]);
                                setMinPrice("");
                                setMaxPrice("");
                                setMinArea("");
                                setMaxArea("");
                                setFacing("");
                                setSearchParams({ type: "plot" });
                                setMobileFilterOpen(false);
                            }}
                        >
                            Reset All
                        </button>
                    </div>

                    {/* Plot Type */}
                    <div className="mb-8">
                        <label className="text-sm font-semibold text-gray-700 block mb-3">Plot Type</label>
                        <div className="flex flex-wrap gap-2">
                            {["Residential Plot", "Commercial Plot", "Industrial Plot"].map((type) => {
                                const isSelected = selectedSubTypes.includes(type) || searchParams.get("plot_type") === type;
                                return (
                                    <button
                                        key={type}
                                        onClick={() => {
                                            toggleSubType(type);
                                            const currentPt = searchParams.get("plot_type");
                                            handleFilterChange("plot_type", currentPt === type ? "" : type);
                                        }}
                                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${isSelected
                                            ? "bg-blue-600 border-blue-600 text-white shadow-md shadow-blue-100 font-semibold"
                                            : "bg-white border-gray-200 text-gray-600 hover:border-blue-300 hover:bg-blue-50/50"
                                            }`}
                                    >
                                        {type}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Approval Status */}
                    <div className="mb-8">
                        <label className="text-sm font-semibold text-gray-700 block mb-3">Approval Status</label>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => {
                                    const isDTCP = searchParams.get("dtcp") === "true";
                                    handleFilterChange("dtcp", isDTCP ? "" : "true");
                                }}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${searchParams.get("dtcp") === "true"
                                    ? "bg-blue-600 border-blue-600 text-white shadow-md shadow-blue-100 font-semibold"
                                    : "bg-white border-gray-200 text-gray-600 hover:border-blue-300 hover:bg-blue-50/50"
                                    }`}
                            >
                                DTCP Approved
                            </button>
                            <button
                                onClick={() => {
                                    const isCMDA = searchParams.get("cmda") === "true";
                                    handleFilterChange("cmda", isCMDA ? "" : "true");
                                }}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${searchParams.get("cmda") === "true"
                                    ? "bg-blue-600 border-blue-600 text-white shadow-md shadow-blue-100 font-semibold"
                                    : "bg-white border-gray-200 text-gray-600 hover:border-blue-300 hover:bg-blue-50/50"
                                    }`}
                            >
                                CMDA Approved
                            </button>
                            <button
                                onClick={() => {
                                    const isRERA = searchParams.get("rera") === "true";
                                    handleFilterChange("rera", isRERA ? "" : "true");
                                }}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${searchParams.get("rera") === "true"
                                    ? "bg-blue-600 border-blue-600 text-white shadow-md shadow-blue-100 font-semibold"
                                    : "bg-white border-gray-200 text-gray-600 hover:border-blue-300 hover:bg-blue-50/50"
                                    }`}
                            >
                                RERA Registered
                            </button>
                        </div>
                    </div>

                    {/* Location Search */}
                    <div className="mb-8">
                        <label className="text-sm font-semibold text-gray-700 block mb-3">Location</label>
                        <div className="relative">
                            <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="City"
                                value={searchParams.get("keyword") || ""}
                                onChange={(e) => handleFilterChange("keyword", e.target.value)}
                                className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-100 outline-none"
                            />
                        </div>
                    </div>

                    {/* Facing Direction */}
                    <div className="mb-8">
                        <label className="text-sm font-semibold text-gray-700 block mb-3">Facing Direction</label>
                        <div className="flex flex-wrap gap-2">
                            {["East", "West", "North", "South", "NE", "NW", "SE", "SW"].map((dir) => (
                                <button
                                    key={dir}
                                    onClick={() => {
                                        const newVal = facing === dir ? "" : dir;
                                        setFacing(newVal);
                                        handleFilterChange("facing_direction", newVal);
                                    }}
                                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${facing === dir
                                        ? "bg-blue-600 border-blue-600 text-white shadow-md shadow-blue-100 font-semibold"
                                        : "bg-white border-gray-200 text-gray-600 hover:border-blue-300 hover:bg-blue-50/50"
                                        }`}
                                >
                                    {dir}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Budget Filter */}
                    <div className="mb-8">
                        <label className="text-sm font-semibold text-gray-700 block mb-3">Budget (₹)</label>
                        <div className="grid grid-cols-2 gap-2">
                            <input
                                type="number"
                                placeholder="Min"
                                value={minPrice}
                                onChange={(e) => {
                                    setMinPrice(e.target.value);
                                    handleFilterChange("minPrice", e.target.value);
                                }}
                                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-100"
                            />
                            <input
                                type="number"
                                placeholder="Max"
                                value={maxPrice}
                                onChange={(e) => {
                                    setMaxPrice(e.target.value);
                                    handleFilterChange("maxPrice", e.target.value);
                                }}
                                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-100"
                            />
                        </div>
                    </div>

                    {/* Plot Area Filter */}
                    <div className="mb-8">
                        <label className="text-sm font-semibold text-gray-700 block mb-3">Plot Area (Sqft/Cent)</label>
                        <div className="grid grid-cols-2 gap-2">
                            <input
                                type="number"
                                placeholder="Min"
                                value={minArea}
                                onChange={(e) => {
                                    setMinArea(e.target.value);
                                    handleFilterChange("minArea", e.target.value);
                                }}
                                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-100"
                            />
                            <input
                                type="number"
                                placeholder="Max"
                                value={maxArea}
                                onChange={(e) => {
                                    setMaxArea(e.target.value);
                                    handleFilterChange("maxArea", e.target.value);
                                }}
                                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-100"
                            />
                        </div>
                    </div>

                    {/* Mobile Apply Button */}
                    <button
                        className="lg:hidden w-full py-3 bg-blue-600 text-white rounded-xl font-semibold shadow-lg shadow-blue-100 hover:bg-blue-700 transition mt-4"
                        onClick={() => setMobileFilterOpen(false)}
                    >
                        Apply Filters
                    </button>
                </aside>

                {/* RIGHT SIDE - CONTENT */}
                <div className="flex-1">
                    {/* Results Header */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">
                                Properties ({filteredProperties.length}) {searchParams.get("keyword") ? `in ${searchParams.get("keyword")}` : ""}
                            </h1>
                            {/* Radius Search Info */}
                            {searchMode === 'radius' && searchLocation && (
                                <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
                                    <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                                    <p className="text-xs font-medium text-amber-800">
                                        No exact matches in {searchLocation}. Showing properties within {radiusKm || 5} km radius
                                    </p>
                                </div>
                            )}
                            {searchMode === 'exact' && searchLocation && (
                                <p className="text-gray-500 text-sm mt-1">Showing exact matches in {searchLocation}</p>
                            )}
                            {searchMode !== 'radius' && !searchLocation && (
                                <p className="text-gray-500 text-sm mt-1">Showing the latest verified listings</p>
                            )}
                        </div>

                        <div className="flex items-center gap-2 sm:gap-4">
                            {/* Mobile Filter Button */}
                            <button
                                className="lg:hidden flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition"
                                onClick={() => setMobileFilterOpen(true)}
                            >
                                <Filter size={16} /> Filters
                            </button>

                            {/* Sort Dropdown */}
                            <div className="relative group">
                                <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50">
                                    Sort: Newest <ChevronDown size={14} />
                                </button>
                            </div>

                            {/* View Mode Toggle */}
                            <div className="flex items-center p-1 bg-white border border-gray-200 rounded-xl">
                                <button
                                    onClick={() => setViewMode("list")}
                                    className={`p-1.5 rounded-lg transition ${viewMode === "list" ? "bg-blue-50 text-blue-600" : "text-gray-400 hover:text-gray-600"}`}
                                >
                                    <List size={20} />
                                </button>
                                <button
                                    onClick={() => setViewMode("grid")}
                                    className={`p-1.5 rounded-lg transition ${viewMode === "grid" ? "bg-blue-50 text-blue-600" : "text-gray-400 hover:text-gray-600"}`}
                                >
                                    <Grid size={20} />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Property Items */}
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-24">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                            <p className="text-gray-500 font-medium">Seeking the best results for you...</p>
                        </div>
                    ) : filteredProperties.length > 0 ? (
                        <div className={`grid gap-6 ${viewMode === "grid" ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" : "grid-cols-1"}`}>
                            {filteredProperties.map((property) => (
                                <div
                                    key={property.id}
                                    className={`bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden cursor-pointer transition-all hover:shadow-md hover:-translate-y-1 ${viewMode === "list" ? "flex flex-col md:flex-row h-auto md:h-48" : ""
                                        }`}
                                    onClick={() => navigate(`/property/${property.id}`)}
                                >
                                    {/* Image Container */}
                                    <div className={`relative ${viewMode === "list" ? "w-full md:w-60 h-36 md:h-full" : "h-40"}`}>
                                        <img
                                            src={property.image}
                                            alt={property.title}
                                            className="w-full h-full object-cover"
                                            onError={(e) => e.target.src = "/assets/best-deals/apt1.jpg"}
                                        />
                                        <div className="absolute top-4 left-4 flex gap-2">
                                            <span className={`px-3 py-1 rounded-lg text-[10px] font-bold text-white uppercase shadow-sm ${property.listingType?.toLowerCase() === "sale" ? "bg-green-600" : "bg-blue-600"
                                                }`}>
                                                For {property.listingType || "Sale"}
                                            </span>
                                        </div>
                                        <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur px-2 py-1 rounded-md text-[10px] font-semibold text-gray-700 flex items-center gap-1 shadow-sm">
                                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div> Ducktail Assured
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="p-3 flex-1 flex flex-col justify-between">
                                        <div>
                                            <h3 className="text-sm font-bold text-gray-900 mb-0.5 line-clamp-1">{property.title}</h3>
                                            <div className="flex items-center text-gray-500 text-[10px] mb-2">
                                                <MapPin size={10} className="mr-0.5 flex-shrink-0" />
                                                <span className="line-clamp-1">{property.address}</span>
                                            </div>

                                            <div className="flex items-center gap-2 text-[10px] font-medium text-gray-600">
                                                {property.bedrooms > 0 && (
                                                    <span className="flex items-center gap-1 bg-gray-50 px-1.5 py-0.5 rounded-md border border-gray-100">
                                                        <Bed size={12} className="text-blue-500" /> {property.bedrooms} Beds
                                                    </span>
                                                )}
                                                <span className="flex items-center gap-1 bg-gray-50 px-1.5 py-0.5 rounded-md border border-gray-100">
                                                    <Expand size={12} className="text-blue-500" /> {property.area} {property.areaUnit}
                                                </span>
                                                <span className="flex items-center gap-1 bg-gray-50 px-1.5 py-0.5 rounded-md border border-gray-100">
                                                    <Building2 size={12} className="text-blue-500" /> {property.type || "House"}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="mt-2.5 pt-2.5 border-t border-gray-50 flex items-center justify-between">
                                            <div className="flex items-baseline gap-1">
                                                <span className="text-base font-bold text-red-600">
                                                    ₹{property.price > 0 ? (property.price % 1 === 0 ? property.price.toLocaleString("en-IN") : property.price.toLocaleString("en-IN")) : "Price on req"}
                                                </span>
                                                {property.listingType?.toLowerCase() === "rent" && (
                                                    <span className="text-gray-400 text-[9px] uppercase font-bold">/Month</span>
                                                )}
                                                {property.price >= 10000000 && <span className="text-gray-500 text-[10px] ml-0.5">(Cr)</span>}
                                                {property.price >= 100000 && property.price < 10000000 && <span className="text-gray-500 text-[10px] ml-0.5">(L)</span>}
                                            </div>
                                            <span className="text-[9px] text-gray-400 font-medium self-end mb-0.5">ID: {property.id}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-32 bg-white rounded-3xl border border-dashed border-gray-200">
                            <div className="p-4 bg-gray-50 rounded-full mb-4">
                                <Search size={40} className="text-gray-300" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-1">No matches found</h3>
                            <p className="text-gray-500 max-w-xs text-center">Try adjusting your filters or search keywords to find what you're looking for.</p>
                            <button
                                onClick={() => setSearchParams({ type: "house", filter: "sale" })}
                                className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-xl font-semibold shadow-lg shadow-blue-100 hover:bg-blue-700 transition"
                            >
                                Clear All Filters
                            </button>
                        </div>
                    )}
                </div>
            </main>

        </div>
    );
};

export default PropertySearch;
